import { useState } from "react";
import { ExternalLink, Link2, Plus, Trash2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { web, errMsg } from "../../lib/api";
import { Btn } from "./kit";
import { TextInput } from "./Form";

/** YouTube and Vimeo links play inside the page; everything else (Hudl, Drive...) opens in a new tab. */
export const embedOf = (raw) => {
  try {
    const u = new URL(raw);
    const h = u.hostname.replace(/^www\.|^m\./, "");
    let id = null;
    if (h === "youtu.be") id = u.pathname.slice(1);
    else if (h.endsWith("youtube.com")) id = u.searchParams.get("v") || u.pathname.match(/^\/(shorts|embed|live)\/([^/?]+)/)?.[2];
    if (id && /^[\w-]{6,20}$/.test(id)) return `https://www.youtube-nocookie.com/embed/${id}`;
    const vm = h === "vimeo.com" && u.pathname.match(/^\/(\d+)/);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  } catch {
    return null;
  }
  return null;
};

export const hostOf = (raw) => { try { return new URL(raw).hostname.replace(/^www\./, ""); } catch { return "link"; } };

export const LinkPlayer = ({ url, title }) => {
  const embed = embedOf(url);
  if (embed) return <iframe data-testid="play-link-embed" src={embed} title={title} className="aspect-video w-full" allow="accelerometer; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />;
  return (
    <a data-testid="play-link-open" href={url} target="_blank" rel="noreferrer" className="flex aspect-video w-full flex-col items-center justify-center gap-3 text-slate-200 hover:text-[#39FF14]">
      <PlayCircle className="h-14 w-14 text-[#39FF14]" />
      <span className="font-display text-lg font-bold uppercase">Watch on {hostOf(url)}</span>
      <span className="flex items-center gap-1 text-xs text-slate-400"><ExternalLink className="h-3 w-3" />Opens in a new tab</span>
    </a>
  );
};

const AddLink = ({ tid, play, onChanged }) => {
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    if (!/^https:\/\/\S+\.\S+/.test(url.trim())) return toast.error("Paste a full video link that starts with https://");
    setBusy(true);
    try {
      await web.post(`/teams/${tid}/plays/${play.id}/links`, { url: url.trim(), label: label.trim() });
      setUrl(""); setLabel("");
      toast.success("Video link added");
      onChanged();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={add} className="mt-3 grid gap-2 sm:grid-cols-[1fr_10rem_auto]">
      <TextInput data-testid="play-extra-link-url" type="url" inputMode="url" value={url} maxLength={1000} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtu.be/..." />
      <TextInput data-testid="play-extra-link-label" value={label} maxLength={80} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional)" />
      <Btn type="submit" variant="ghost" disabled={busy} data-testid="play-extra-link-add"><Plus className="h-4 w-4" />Add link</Btn>
    </form>
  );
};

/** Extra video links under any play (game film, NBA examples, teaching clips). */
export const PlayLinks = ({ tid, play, canEdit, onChanged }) => {
  const links = Array.isArray(play?.links) ? play.links : [];
  const remove = async (l) => {
    try { await web.delete(`/teams/${tid}/plays/${play.id}/links/${l.id}`); toast.success("Link removed"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!links.length && !canEdit) return null;
  return (
    <div data-testid="play-links" className="rounded-xl border border-[#1E293B] bg-[#0F172A] p-4">
      <p className="flex items-center gap-2 font-mono-gd text-xs uppercase tracking-wider text-slate-300"><Link2 className="h-3.5 w-3.5 text-[#39FF14]" />Video links</p>
      {links.length ? (
        <ul className="mt-2 space-y-1.5">
          {links.map((l) => (
            <li key={l.id} data-testid={`play-link-${l.id}`} className="flex items-center gap-2 text-sm">
              <a href={l.url} target="_blank" rel="noreferrer" className="flex min-w-0 flex-1 items-center gap-2 text-white hover:text-[#39FF14]">
                <PlayCircle className="h-4 w-4 shrink-0 text-[#39FF14]" /><span className="truncate">{l.label || hostOf(l.url)}</span>
                <span className="shrink-0 text-xs text-slate-400">{hostOf(l.url)}</span>
              </a>
              {canEdit && <button type="button" aria-label="Remove link" data-testid={`play-link-remove-${l.id}`} onClick={() => remove(l)} className="rounded-full p-1.5 text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>}
            </li>
          ))}
        </ul>
      ) : <p className="mt-2 text-sm text-slate-400">Add game film or teaching clips that go with this play.</p>}
      {canEdit && <AddLink tid={tid} play={play} onChanged={onChanged} />}
    </div>
  );
};
